import { NgxsDataEntityCollectionsRepository } from '@angular-ru/ngxs/repositories'
import { EntityComparator, EntityIdType } from '@angular-ru/cdk/entity'
import { Computed, DataAction, Payload } from '@angular-ru/ngxs/decorators'
import { distinctUntilChanged, distinctUntilKeyChanged, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { GqlPageInfos } from '@wm-front/shared/interfaces'
import { ExtendedEntityCollectionRepositoryOptions } from './extended-entity-collection-repository-options'

/**
 * This class extends the NgxsDataEntityCollectionsRepository because
 * we have some properties and method we use for all our repositories such as
 * loading, error, current...
 * This abstract class already implements these methods and add these properties
 * to the store.
 *
 * If you would like to extend the default Collection Store, with additional
 * values, you can pass your custom interface to the C Generic
 *
 * example:
 * ```typescript
 * interface YourStateStore extends ExtendedEntityCollectionRepositoryOptions {
 *   additionalValues: {},
 * }
 * export class YourState extends BaseEntityCollectionRepository<
 *   YourStateEntity,
 *   string,
 *   YourStateStore
 * >
 * ```
 */
export abstract class BaseEntityCollectionRepository<
  V,
  K extends number | string = EntityIdType,
  C extends ExtendedEntityCollectionRepositoryOptions = ExtendedEntityCollectionRepositoryOptions
> extends NgxsDataEntityCollectionsRepository<V, K, C> {
  protected constructor() {
    super()
  }

  /**
   * Returns the current entity
   */
  @Computed()
  public get currentEntity(): V | undefined {
    const current = this.snapshot.current
    if (current) {
      return this.snapshot.entities[current]
    }
    return undefined
  }

  /**
   * Returns the current an observable of the current entity
   */
  @Computed()
  public get currentEntity$(): Observable<V | undefined> {
    return this.state$.pipe(
      distinctUntilChanged(
        (previous, current) =>
          (previous.current
            ? JSON.stringify(previous.entities[previous.current])
            : undefined) ===
          (current.current
            ? JSON.stringify(current.entities[current.current])
            : undefined)
      ),
      map((state) =>
        state.current ? state.entities[state.current] : undefined
      )
    )
  }

  /**
   * Returns the loading status as an observable
   */
  @Computed()
  public get loading$(): Observable<boolean> {
    return this.state$.pipe(
      distinctUntilKeyChanged('loading'),
      map((state) => state.loading)
    )
  }

  /**
   * Returns the loading status as an observable
   */
  @Computed()
  public get error$(): Observable<Error | null> {
    return this.state$.pipe(
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous.error) === JSON.stringify(current.error)
      ),
      map((state) => state.error)
    )
  }

  /**
   * Returns the page infos as an observable
   */
  @Computed()
  public get pageInfos$(): Observable<GqlPageInfos | null> {
    return this.state$.pipe(
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous?.pageInfos) ===
          JSON.stringify(current?.pageInfos)
      ),
      map((state) => state?.pageInfos)
    )
  }

  /**
   * Returns the total count as an observable
   */
  @Computed()
  public get totalCount$(): Observable<number | null> {
    return this.state$.pipe(
      distinctUntilKeyChanged('totalCount'),
      map((state) => state.totalCount)
    )
  }

  /**
   * Set the loading of the entity state to true of false
   */
  @DataAction()
  public setLoading(@Payload('loading') loading: boolean): void {
    const state = this.getState()
    this.setEntitiesState({
      ...state,
      loading,
    })
  }

  /**
   * Set any Error that could happen with the entity api
   */
  @DataAction()
  public setError(@Payload('error') error: Error | null): void {
    const state = this.getState()
    this.setEntitiesState({
      ...state,
      error: error as any,
    })
  }

  /**
   * Set the page infos
   */
  @DataAction()
  public setPageInfos(
    @Payload('pageInfos') pageInfos: GqlPageInfos | null
  ): void {
    const state = this.getState()
    this.setEntitiesState({
      ...state,
      pageInfos,
    })
  }

  /**
   * Set the page infos
   */
  @DataAction()
  public setTotalCount(@Payload('totalCount') totalCount: number | null): void {
    const state = this.getState()
    this.setEntitiesState({
      ...state,
      totalCount,
    })
  }

  /**
   * Set the current entity
   * @param {string | number} id of the entity
   */
  @DataAction()
  public setCurrentEntity(@Payload('id') id?: string | number): void {
    const state = this.getState()
    this.setEntitiesState({
      ...state,
      current: id,
    })
  }

  /**
   * This method is there only to remove an error related
   * to the return type of the parent setComparator method
   * https://github.com/microsoft/TypeScript/issues/19661
   * @param {EntityComparator<V> | null} comparator
   * @returns {any}
   */
  public override setComparator(comparator: EntityComparator<V> | null): any {
    return super.setComparator(comparator)
  }
}
